import { Box, Stack, Typography, Breadcrumbs, Link as MuiLink, IconButton, Tooltip } from "@mui/material";
import { HomeRounded, ArrowBackRounded } from "@mui/icons-material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import PageGuide from "../PageGuide";

/**
 * PageHeader — standardized page header used across every page.
 * Breadcrumb (Home + trail) → H4 title + optional action → "About this page" brief.
 *
 *   <PageHeader
 *     breadcrumbs={[{ label: "Tasks" }]}          // Home is added automatically
 *     title="Tasks"
 *     action={<Button>New</Button>}
 *     guide={{ why, what, how, when }}
 *   />
 */
export default function PageHeader({ breadcrumbs = [], title, action, guide, showBack = true, sx = {} }) {
  const navigate = useNavigate();
  const crumbs = [{ label: "Home", to: "/dashboard/app", home: true }, ...breadcrumbs];
  return (
    <Box sx={{ mb: 1, ...sx }}>
      <Breadcrumbs sx={{ mb: 1.5 }} aria-label="breadcrumb">
        {crumbs.map((b, i) => {
          const last = i === crumbs.length - 1;
          if (last || !b.to) {
            return (
              <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
                {b.home && <HomeRounded fontSize="small" />}
                <Typography color="text.primary">{b.label}</Typography>
              </Stack>
            );
          }
          return (
            <MuiLink key={i} component={RouterLink} to={b.to} color="inherit" underline="hover"
              sx={{ display: "flex", alignItems: "center" }}>
              {b.home && <HomeRounded fontSize="small" sx={{ mr: 0.5 }} />}{b.label}
            </MuiLink>
          );
        })}
      </Breadcrumbs>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: guide ? 1 : 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {showBack && (
            <Tooltip title="Back">
              <IconButton size="small" onClick={() => navigate(-1)} aria-label="Go back"
                sx={{ border: "1px solid", borderColor: "divider", bgcolor: "background.paper",
                  "&:hover": { bgcolor: "action.hover" } }}>
                <ArrowBackRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="h4" sx={{ fontWeight: 600 }}>{title}</Typography>
        </Stack>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Stack>
      {guide && <PageGuide {...guide} />}
    </Box>
  );
}
